import { useAuth } from "@elit-hair/elit-tools";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DatePicker,
    Icon,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import { TbChartBar, TbClock, TbHourglass, TbPhone, TbPhoneCall, TbPhoneCheck, TbPhoneX } from "react-icons/tb";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { CustomTooltip } from "@/components/charts/custom-tooltip";
import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { useTeamMembersKpiByDateRange, useUserKpiByDateRange } from "@/hooks/queries/kpi.queries";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { CALL_PURPOSES } from "@/pages/performance/quality-watch/utils/quality-watch.constants";
import type { KpiData, TeamMembersKpiResponse } from "@/types/call.types";
import { fadeIn, transitions } from "@/utils/animations";
import { formatDate } from "@/utils/format-date";
import { formatDuration } from "@/utils/format-duration";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { getAccessibleTeams } from "@/utils/team.utils";

import { Route as CallVolumeRoute, type CallVolumeSearch } from "./route";

const CHART_COLORS = {
    total: "var(--chart-1)",
    brutto: "var(--chart-2)",
    netto: "var(--chart-4)",
    inCallTime: "var(--chart-3)",
    averageCallDuration: "var(--chart-5)",
} as const;

const DURATION_DATA_KEYS = new Set(["inCallTime", "averageCallDuration"]);

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_CALL_VOLUME)({
    component: CallVolumePage,
});

const ALL_USERS_VALUE = "all";
const ALL_PURPOSES_VALUE = "all";

type DailyCallVolume = {
    date: string;
    total: number;
    brutto: number;
    netto: number;
    inCallTime: number;
    averageCallDuration: number;
};

function aggregateTeamKpiData(data: Record<string, TeamMembersKpiResponse>, agentId?: string): DailyCallVolume[] {
    return Object.entries(data)
        .map(([date, teamResponse]) => {
            const members = agentId
                ? teamResponse.members.filter((member) => member.userId === agentId)
                : teamResponse.members;

            const totals = members.reduce(
                (
                    acc: { total: number; brutto: number; netto: number; inCallTime: number },
                    member: { kpiData: KpiData }
                ) => {
                    acc.total += member.kpiData.all ?? 0;
                    acc.brutto += member.kpiData.bruttos ?? 0;
                    acc.netto += member.kpiData.netto ?? 0;
                    acc.inCallTime += member.kpiData.inCallTime ?? 0;
                    return acc;
                },
                { total: 0, brutto: 0, netto: 0, inCallTime: 0 }
            );

            return {
                date,
                ...totals,
                averageCallDuration: totals.total > 0 ? totals.inCallTime / totals.total : 0,
            };
        })
        .sort((a, b) => a.date.localeCompare(b.date));
}

function userKpiDataToChart(data: Record<string, KpiData>): DailyCallVolume[] {
    return Object.entries(data)
        .map(([date, kpi]) => ({
            date,
            total: kpi.all ?? 0,
            brutto: kpi.bruttos ?? 0,
            netto: kpi.netto ?? 0,
            inCallTime: kpi.inCallTime ?? 0,
            averageCallDuration: kpi.averageCallDuration ?? 0,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

const { useSearch, useNavigate, to } = CallVolumeRoute;

function CallVolumePage() {
    const { t } = useTranslation();
    const { userData } = useAuth();
    const userPermissionCodes = userData?.permissionCodes || [];

    const search = useSearch({ structuralSharing: true });
    const navigate = useNavigate();

    const updateSearch = useCallback(
        (updates: Partial<CallVolumeSearch>) => {
            navigate({ to, search: (prev) => ({ ...prev, ...updates }), replace: true });
        },
        [navigate]
    );

    const selectedTeamId = search.team ?? ALL_USERS_VALUE;
    const selectedAgentId = search.agent;
    const selectedCallPurpose = search.callPurpose;
    const startDate = useMemo(
        () =>
            search.start && DateTime.fromISO(search.start).isValid
                ? DateTime.fromISO(search.start)
                : DateTime.now().minus({ days: 30 }),
        [search.start]
    );
    const endDate = useMemo(
        () => (search.end && DateTime.fromISO(search.end).isValid ? DateTime.fromISO(search.end) : DateTime.now()),
        [search.end]
    );

    const { data: teamsData, isLoading: isLoadingTeams } = useTeams();
    const accessibleTeams = useMemo(
        () => getAccessibleTeams(teamsData?.data || [], userPermissionCodes, userData?.id),
        [teamsData?.data, userPermissionCodes, userData?.id]
    );

    const setSelectedTeamId = useCallback(
        (teamId: string) => updateSearch({ team: teamId === ALL_USERS_VALUE ? undefined : teamId }),
        [updateSearch]
    );

    const handleAgentChange = useCallback(
        (agentId: string | null) => {
            updateSearch({ agent: agentId ?? undefined });
        },
        [updateSearch]
    );

    const handleCallPurposeChange = useCallback(
        (purpose: string) => {
            updateSearch({ callPurpose: purpose === ALL_PURPOSES_VALUE ? undefined : purpose });
        },
        [updateSearch]
    );

    const handleStartDateChange = useCallback(
        (date: Date | undefined) => {
            if (date) updateSearch({ start: DateTime.fromJSDate(date).toFormat("yyyy-MM-dd") });
        },
        [updateSearch]
    );

    const handleEndDateChange = useCallback(
        (date: Date | undefined) => {
            if (date) updateSearch({ end: DateTime.fromJSDate(date).toFormat("yyyy-MM-dd") });
        },
        [updateSearch]
    );

    const {
        data: teamKpiData,
        isLoading: isTeamKpiLoading,
        isError: isTeamKpiError,
    } = useTeamMembersKpiByDateRange(startDate, endDate, selectedTeamId, selectedCallPurpose, {
        enabled: !selectedAgentId,
    });

    const {
        data: agentKpiData,
        isLoading: isAgentKpiLoading,
        isError: isAgentKpiError,
    } = useUserKpiByDateRange(startDate, endDate, selectedAgentId, selectedCallPurpose, {
        enabled: !!selectedAgentId,
    });

    const chartData = useMemo<DailyCallVolume[]>(() => {
        if (selectedAgentId) {
            if (!agentKpiData || typeof agentKpiData !== "object") return [];
            return userKpiDataToChart(agentKpiData);
        }

        if (!teamKpiData || typeof teamKpiData !== "object") return [];
        return aggregateTeamKpiData(teamKpiData);
    }, [selectedAgentId, agentKpiData, teamKpiData]);

    const summary = useMemo(() => {
        if (chartData.length === 0) {
            return {
                totalCalls: 0,
                totalBrutto: 0,
                totalNetto: 0,
                avgPerDay: 0,
                totalInCallTime: 0,
                averageCallDuration: 0,
            };
        }

        const totalCalls = chartData.reduce((sum, d) => sum + d.total, 0);
        const totalBrutto = chartData.reduce((sum, d) => sum + d.brutto, 0);
        const totalNetto = chartData.reduce((sum, d) => sum + d.netto, 0);
        const totalInCallTime = chartData.reduce((sum, d) => sum + d.inCallTime, 0);
        const avgPerDay = chartData.length > 0 ? Math.round(totalCalls / chartData.length) : 0;
        const averageCallDuration = totalCalls > 0 ? totalInCallTime / totalCalls : 0;

        return { totalCalls, totalBrutto, totalNetto, avgPerDay, totalInCallTime, averageCallDuration };
    }, [chartData]);

    const isLoading = isLoadingTeams || (selectedAgentId ? isAgentKpiLoading : isTeamKpiLoading);
    const isError = selectedAgentId ? isAgentKpiError : isTeamKpiError;

    const selectedTeamName =
        selectedTeamId === ALL_USERS_VALUE
            ? t("all_users")
            : (accessibleTeams.find((team) => team.id === selectedTeamId)?.name ?? "");

    const selectedAgentName = useMemo(() => {
        if (!selectedAgentId) return null;

        for (const team of accessibleTeams) {
            const member = team.members.find((m) => m.id === selectedAgentId);
            if (member) return member.user?.name ?? selectedAgentId;
        }

        return selectedAgentId;
    }, [selectedAgentId, accessibleTeams]);

    const chartSubtitle = selectedAgentName ?? selectedTeamName;

    const formatTooltipValue = (value: string | number, dataKey?: string | number) => {
        if (typeof dataKey === "string" && DURATION_DATA_KEYS.has(dataKey)) {
            return formatDuration(Number(value));
        }
        return String(value);
    };

    return (
        <div className="flex flex-col gap-4 p-4 h-full w-full overflow-auto">
            <div className="flex flex-wrap justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Select value={selectedTeamId} onValueChange={setSelectedTeamId} disabled={!!selectedAgentId}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder={t("select_team")} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_USERS_VALUE}>
                                <div className="flex items-center gap-2 font-medium">{t("all_users")}</div>
                            </SelectItem>
                            {accessibleTeams.map((team) => (
                                <SelectItem key={team.id} value={team.id}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: team.color }} />
                                        {team.name}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <div className="w-[280px]">
                        <TeamMemberSelector
                            selectedMemberId={selectedAgentId ?? null}
                            onMemberChange={handleAgentChange}
                            labels={{ resetSelection: "all_users" }}
                        />
                    </div>

                    <Select value={selectedCallPurpose ?? ALL_PURPOSES_VALUE} onValueChange={handleCallPurposeChange}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder={t("call_purpose")} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_PURPOSES_VALUE}>{t("all_call_purposes")}</SelectItem>
                            {Object.entries(CALL_PURPOSES).map(([key, label]) => (
                                <SelectItem key={key} value={label}>
                                    {label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{t("from")}</span>
                        <DatePicker date={startDate.toJSDate()} onDateChange={handleStartDateChange} />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{t("to")}</span>
                        <DatePicker date={endDate.toJSDate()} onDateChange={handleEndDateChange} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <SummaryCard
                    title={t("total_calls")}
                    value={summary.totalCalls}
                    icon={TbPhone}
                    chartColor={CHART_COLORS.total}
                />
                <SummaryCard
                    title={t("brutto")}
                    value={summary.totalBrutto}
                    icon={TbPhoneCall}
                    chartColor={CHART_COLORS.brutto}
                />
                <SummaryCard
                    title={t("netto")}
                    value={summary.totalNetto}
                    icon={TbPhoneCheck}
                    chartColor={CHART_COLORS.netto}
                />
                <SummaryCard
                    title={t("avg_calls_per_day")}
                    value={summary.avgPerDay}
                    icon={TbPhoneX}
                    chartColor="var(--chart-5)"
                />
                <SummaryDurationCard
                    title={t("in_call_time")}
                    value={formatDuration(summary.totalInCallTime)}
                    icon={TbClock}
                    chartColor={CHART_COLORS.inCallTime}
                />
                <SummaryDurationCard
                    title={t("avg_call_duration")}
                    value={formatDuration(summary.averageCallDuration)}
                    icon={TbHourglass}
                    chartColor={CHART_COLORS.averageCallDuration}
                />
            </div>

            <motion.div {...fadeIn} transition={transitions.smooth} className="flex-1 min-h-0">
                <Card className="h-full overflow-hidden border border-border/50 shadow-sm hover:shadow-md transition-all duration-200 bg-card/50 backdrop-blur-sm">
                    <CardHeader className="pb-1 px-3 pt-3">
                        <div className="flex items-center gap-2">
                            <div className="p-1 rounded-md bg-muted">
                                <Icon icon={TbChartBar} />
                            </div>
                            <CardTitle className="text-sm font-semibold text-foreground">
                                {t("calls_per_day")} {chartSubtitle ? `- ${chartSubtitle}` : ""}
                            </CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="relative pt-1 px-1 pb-1">
                        <div className="relative w-full h-[450px]">
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center z-10">
                                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                        <Spinner />
                                        {t("loading")}
                                    </div>
                                </div>
                            )}

                            {!isLoading && isError && (
                                <div className="absolute inset-0 flex items-center justify-center z-10">
                                    <div className="text-destructive text-sm">{t("error_loading_data")}</div>
                                </div>
                            )}

                            {!isLoading && !isError && chartData.length === 0 && (
                                <div className="absolute inset-0 flex items-center justify-center z-10">
                                    <div className="text-muted-foreground text-sm">{t("no_data_available")}</div>
                                </div>
                            )}

                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 5, bottom: 30 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                    <XAxis
                                        dataKey="date"
                                        stroke="var(--border)"
                                        fontSize={12}
                                        tickFormatter={formatDate}
                                        tick={{ fill: "var(--muted-foreground)" }}
                                        tickLine={{ stroke: "var(--border)" }}
                                    />
                                    <YAxis
                                        yAxisId="calls"
                                        stroke="var(--border)"
                                        fontSize={12}
                                        allowDecimals={false}
                                        tick={{ fill: "var(--muted-foreground)" }}
                                        tickLine={{ stroke: "var(--border)" }}
                                    />
                                    <YAxis
                                        yAxisId="duration"
                                        orientation="right"
                                        stroke="var(--border)"
                                        fontSize={12}
                                        tickFormatter={(value) => formatDuration(value)}
                                        tick={{ fill: "var(--muted-foreground)" }}
                                        tickLine={{ stroke: "var(--border)" }}
                                    />
                                    <Tooltip
                                        content={
                                            <CustomTooltip
                                                valueFormatter={(value, dataKey) => formatTooltipValue(value, dataKey)}
                                            />
                                        }
                                        cursor={{ fill: "var(--muted)", opacity: 0.4 }}
                                    />
                                    <Legend
                                        wrapperStyle={{ fontSize: 13, paddingTop: 8 }}
                                        iconType="circle"
                                        iconSize={10}
                                    />
                                    <Bar
                                        yAxisId="calls"
                                        dataKey="total"
                                        name={t("total_calls")}
                                        fill={CHART_COLORS.total}
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar
                                        yAxisId="calls"
                                        dataKey="brutto"
                                        name={t("brutto")}
                                        fill={CHART_COLORS.brutto}
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar
                                        yAxisId="calls"
                                        dataKey="netto"
                                        name={t("netto")}
                                        fill={CHART_COLORS.netto}
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Line
                                        yAxisId="duration"
                                        type="monotone"
                                        dataKey="inCallTime"
                                        name={t("in_call_time")}
                                        stroke={CHART_COLORS.inCallTime}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        yAxisId="duration"
                                        type="monotone"
                                        dataKey="averageCallDuration"
                                        name={t("avg_call_duration")}
                                        stroke={CHART_COLORS.averageCallDuration}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}

type SummaryCardProps = {
    title: string;
    value: number;
    icon: IconType;
    chartColor: string;
};

function SummaryCard({ title, value, icon: CardIcon, chartColor }: SummaryCardProps) {
    return (
        <motion.div {...fadeIn} transition={transitions.smooth}>
            <Card className="border border-border/50 shadow-sm bg-card/50 backdrop-blur-sm">
                <CardContent className="flex items-center gap-3 p-4">
                    <div className="relative p-2 rounded-lg">
                        <div
                            className="absolute inset-0 rounded-lg opacity-15"
                            style={{ backgroundColor: chartColor }}
                        />
                        <CardIcon className="relative h-5 w-5" style={{ color: chartColor }} />
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">{title}</p>
                        <p className="text-2xl font-bold text-foreground">{value.toLocaleString()}</p>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

type SummaryDurationCardProps = {
    title: string;
    value: string;
    icon: IconType;
    chartColor: string;
};

function SummaryDurationCard({ title, value, icon: CardIcon, chartColor }: SummaryDurationCardProps) {
    return (
        <motion.div {...fadeIn} transition={transitions.smooth}>
            <Card className="border border-border/50 shadow-sm bg-card/50 backdrop-blur-sm">
                <CardContent className="flex items-center gap-3 p-4">
                    <div className="relative p-2 rounded-lg">
                        <div
                            className="absolute inset-0 rounded-lg opacity-15"
                            style={{ backgroundColor: chartColor }}
                        />
                        <CardIcon className="relative h-5 w-5" style={{ color: chartColor }} />
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">{title}</p>
                        <p className="text-2xl font-bold text-foreground">{value}</p>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
